import * as React from 'react';

import {
  enumScaleConnectionType,
  enumScaleFormatType,
  enumScaleMeasurementMode,
  enumSidecarSortOption,
  type ScaleConnectionType,
  type ScaleFormatType,
  type ScaleMeasurementMode,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  DEFAULT_WEIGHT_UNITS,
  Select,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { scaleCreateAtom } from '../../../_graphql/mutationScaleCreate';
import { camerasListQueryAtom } from '../../../_graphql/queryCamerasList';
import { sidecarsListQueryAtom } from '../../../_graphql/querySidecarsList';
import { useTranslations } from '../../../i18n/useTranslations';

interface ScaleCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const $sidecarsForPicker = sidecarsListQueryAtom(() => ({
  variables: { sorting: [enumSidecarSortOption.NameAsc], limit: 100 },
}));

export const ScaleCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: ScaleCreateDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const [connectionType, setConnectionType] =
    React.useState<ScaleConnectionType>(enumScaleConnectionType.TCP);
  const [tcpHost, setTcpHost] = React.useState('');
  const [tcpPort, setTcpPort] = React.useState('');
  const [serialPortPath, setSerialPortPath] = React.useState('');
  const [baudRate, setBaudRate] = React.useState('9600');
  const [dataBits, setDataBits] = React.useState('8');
  const [stopBits, setStopBits] = React.useState('1');
  const [parity, setParity] = React.useState('None');
  const [scaleFormatType, setScaleFormatType] = React.useState<ScaleFormatType>(
    enumScaleFormatType.MettlerToledo,
  );
  const [measurementMode, setMeasurementMode] =
    React.useState<ScaleMeasurementMode>(enumScaleMeasurementMode.Continuous);
  const [onDemandString, setOnDemandString] = React.useState('');
  const [unitOfMeasurement, setUnitOfMeasurement] = React.useState<
    string | null
  >(null);
  const [sidecarId, setSidecarId] = React.useState<string | null>(null);
  const [cameraId, setCameraId] = React.useState<string | null>(null);

  const createScale = useSetAtom(scaleCreateAtom({}));
  const { data: sidecarsData } = useAtomValue($sidecarsForPicker);
  const sidecars = sidecarsData?.sidecars ?? [];

  const selectedSidecar = React.useMemo(
    () => sidecars.find((s) => s.id === sidecarId),
    [sidecars, sidecarId],
  );

  const $facilityCameras = React.useMemo(
    () =>
      camerasListQueryAtom(() => ({
        variables: {
          facilityId: selectedSidecar?.facilityId ?? undefined,
        },
      })),
    [selectedSidecar?.facilityId],
  );
  const { data: camerasData } = useAtomValue($facilityCameras);
  const cameraOptions = React.useMemo(
    () =>
      camerasData?.cameras?.map((c) => ({ key: c.id, label: c.name })) ?? [],
    [camerasData],
  );

  const sidecarOptions = React.useMemo(
    () =>
      sidecars.map((s) => ({
        key: s.id,
        label: s.name,
      })),
    [sidecars],
  );

  const connectionTypeOptions = React.useMemo(
    () => [
      {
        key: enumScaleConnectionType.TCP,
        label:
          t.hardwareView.scaleCreateDialog.fields.connectionTypeOptions.TCP,
      },
      {
        key: enumScaleConnectionType.Serial,
        label:
          t.hardwareView.scaleCreateDialog.fields.connectionTypeOptions.Serial,
      },
    ],
    [t],
  );

  const measurementModeOptions = React.useMemo(
    () => [
      {
        key: enumScaleMeasurementMode.Continuous,
        label:
          t.hardwareView.scaleCreateDialog.fields.measurementModeOptions
            .Continuous,
      },
      {
        key: enumScaleMeasurementMode.OnDemand,
        label:
          t.hardwareView.scaleCreateDialog.fields.measurementModeOptions
            .OnDemand,
      },
    ],
    [t],
  );

  const scaleFormatTypeOptions = Object.values(enumScaleFormatType).map(
    (v) => ({ key: v, label: v }),
  );

  const unitOfMeasurementOptions = DEFAULT_WEIGHT_UNITS.map((u) => ({
    key: u.unit,
    label: `${u.name} (${u.symbol})`,
  }));

  const isTCP = connectionType === enumScaleConnectionType.TCP;

  const canSubmit =
    name.trim() !== '' &&
    sidecarId !== null &&
    (isTCP
      ? tcpHost.trim() !== '' && tcpPort.trim() !== ''
      : serialPortPath.trim() !== '');

  const resetForm = React.useCallback(() => {
    setName('');
    setConnectionType(enumScaleConnectionType.TCP);
    setTcpHost('');
    setTcpPort('');
    setSerialPortPath('');
    setBaudRate('9600');
    setDataBits('8');
    setStopBits('1');
    setParity('None');
    setScaleFormatType(enumScaleFormatType.MettlerToledo);
    setMeasurementMode(enumScaleMeasurementMode.Continuous);
    setOnDemandString('');
    setUnitOfMeasurement(null);
    setSidecarId(null);
    setCameraId(null);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || !sidecarId) return;

    try {
      setIsSubmitting(true);
      await createScale({
        sidecarId,
        name: name.trim(),
        connectionType,
        ...(isTCP
          ? {
              tcpHost: tcpHost.trim(),
              tcpPort: parseInt(tcpPort, 10),
            }
          : {
              serialPortPath: serialPortPath.trim(),
              baudRate: parseInt(baudRate, 10) || undefined,
              dataBits: parseInt(dataBits, 10) || undefined,
              stopBits: stopBits || undefined,
              parity: parity || undefined,
            }),
        scaleFormatType,
        measurementMode,
        onDemandString:
          measurementMode === enumScaleMeasurementMode.OnDemand
            ? onDemandString.trim() || undefined
            : undefined,
        unitOfMeasurement: unitOfMeasurement || undefined,
        cameraId: cameraId || undefined,
      });
      toast.success(t.hardwareView.scaleCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.hardwareView.scaleCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    sidecarId,
    createScale,
    name,
    connectionType,
    isTCP,
    tcpHost,
    tcpPort,
    serialPortPath,
    baudRate,
    dataBits,
    stopBits,
    parity,
    scaleFormatType,
    measurementMode,
    onDemandString,
    unitOfMeasurement,
    cameraId,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  const inputClass =
    'rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary';

  return (
    <ConsoleDialog
      className="@ScaleCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.hardwareView.scaleCreateDialog.title}
      content={
        <Col gap3>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.name}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.connectionType}
            </label>
            <Select
              options={connectionTypeOptions}
              value={connectionType}
              onChange={(v) => v && setConnectionType(v)}
            />
          </Col>
          {isTCP && (
            <>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.tcpHost}
                </label>
                <input
                  type="text"
                  value={tcpHost}
                  onChange={(e) => setTcpHost(e.target.value)}
                  placeholder={
                    t.hardwareView.scaleCreateDialog.fields.tcpHostPlaceholder
                  }
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.tcpPort}
                </label>
                <input
                  type="number"
                  value={tcpPort}
                  onChange={(e) => setTcpPort(e.target.value)}
                  placeholder={
                    t.hardwareView.scaleCreateDialog.fields.tcpPortPlaceholder
                  }
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
            </>
          )}
          {!isTCP && (
            <>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.serialPortPath}
                </label>
                <input
                  type="text"
                  value={serialPortPath}
                  onChange={(e) => setSerialPortPath(e.target.value)}
                  placeholder={
                    t.hardwareView.scaleCreateDialog.fields
                      .serialPortPathPlaceholder
                  }
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.baudRate}
                </label>
                <input
                  type="number"
                  value={baudRate}
                  onChange={(e) => setBaudRate(e.target.value)}
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.dataBits}
                </label>
                <input
                  type="number"
                  value={dataBits}
                  onChange={(e) => setDataBits(e.target.value)}
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.stopBits}
                </label>
                <input
                  type="text"
                  value={stopBits}
                  onChange={(e) => setStopBits(e.target.value)}
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
              <Col gap1>
                <label className="text-sm text-neutral/60">
                  {t.hardwareView.scaleCreateDialog.fields.parity}
                </label>
                <input
                  type="text"
                  value={parity}
                  onChange={(e) => setParity(e.target.value)}
                  placeholder="None"
                  className={inputClass}
                  disabled={isSubmitting}
                />
              </Col>
            </>
          )}
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.scaleFormatType}
            </label>
            <Select
              options={scaleFormatTypeOptions}
              value={scaleFormatType}
              onChange={setScaleFormatType}
              isDisabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.measurementMode}
            </label>
            <Select
              options={measurementModeOptions}
              value={measurementMode}
              onChange={(v) => v && setMeasurementMode(v)}
            />
          </Col>
          {measurementMode === enumScaleMeasurementMode.OnDemand && (
            <Col gap1>
              <label className="text-sm text-neutral/60">
                {t.hardwareView.scaleCreateDialog.fields.onDemandString}
              </label>
              <input
                type="text"
                value={onDemandString}
                onChange={(e) => setOnDemandString(e.target.value)}
                className={inputClass}
                disabled={isSubmitting}
              />
            </Col>
          )}
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.unitOfMeasurement}
            </label>
            <Select
              options={unitOfMeasurementOptions}
              value={unitOfMeasurement}
              onChange={setUnitOfMeasurement}
              placeholder={
                t.hardwareView.scaleCreateDialog.fields
                  .unitOfMeasurementPlaceholder
              }
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.scaleCreateDialog.fields.sidecar}
            </label>
            <Select
              options={sidecarOptions}
              value={sidecarId}
              onChange={(v) => {
                setSidecarId(v);
                setCameraId(null);
              }}
              placeholder={
                t.hardwareView.scaleCreateDialog.fields.sidecarPlaceholder
              }
            />
          </Col>
          {sidecarId && cameraOptions.length > 0 && (
            <Col gap1>
              <label className="text-sm text-neutral/60">
                {t.hardwareView.scaleCreateDialog.fields.camera}
              </label>
              <Select
                options={cameraOptions}
                value={cameraId}
                onChange={setCameraId}
                placeholder={
                  t.hardwareView.scaleCreateDialog.fields.cameraPlaceholder
                }
              />
            </Col>
          )}
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.hardwareView.scaleCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.hardwareView.scaleCreateDialog.actions.creating
              : t.hardwareView.scaleCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
